/**
 * Manages health for game entities (player, enemies)
 */
export class HealthSystem {
  private maxHealth: number;
  private currentHealth: number;
  private isDead: boolean = false;
  private onDamageCallbacks: ((amount: number) => void)[] = [];
  private onDeathCallbacks: (() => void)[] = [];
  private onHealCallbacks: ((amount: number) => void)[] = [];
  
  /**
   * Creates a new HealthSystem
   * @param maxHealth The maximum health
   */
  constructor(maxHealth: number) {
    this.maxHealth = maxHealth;
    this.currentHealth = maxHealth;
  }
  
  /**
   * Makes the entity take damage
   * @param amount The amount of damage
   * @returns True if the entity died from this damage
   */
  public takeDamage(amount: number): boolean {
    if (this.isDead) return true;
    
    // Apply damage
    this.currentHealth -= amount;
    
    // Call damage callbacks
    for (const callback of this.onDamageCallbacks) {
      callback(amount);
    }
    
    // Check if dead
    if (this.currentHealth <= 0) {
      this.currentHealth = 0;
      this.isDead = true;
      
      // Call death callbacks
      for (const callback of this.onDeathCallbacks) {
        callback();
      }
      
      return true;
    }
    
    return false;
  }
  
  /**
   * Heals the entity
   * @param amount The amount to heal
   */
  public heal(amount: number): void {
    if (this.isDead) return;
    
    const oldHealth = this.currentHealth;
    this.currentHealth = Math.min(this.currentHealth + amount, this.maxHealth);
    
    const actualHealAmount = this.currentHealth - oldHealth;
    
    // Call heal callbacks if any healing was done
    if (actualHealAmount > 0) {
      for (const callback of this.onHealCallbacks) {
        callback(actualHealAmount);
      }
    }
  }
  
  /**
   * Revives the entity with the specified health percentage
   * @param healthPercentage The percentage of max health to revive with (0-1)
   */
  public revive(healthPercentage: number = 1.0): void {
    this.isDead = false;
    this.currentHealth = Math.max(1, Math.floor(this.maxHealth * healthPercentage));
  }
  
  /**
   * Gets the current health
   * @returns The current health
   */
  public getCurrentHealth(): number {
    return this.currentHealth;
  }
  
  /**
   * Gets the maximum health
   * @returns The maximum health
   */
  public getMaxHealth(): number {
    return this.maxHealth;
  }
  
  /**
   * Gets the health percentage (0-1)
   * @returns The health percentage
   */
  public getHealthPercentage(): number {
    return this.currentHealth / this.maxHealth;
  }
  
  /**
   * Checks if the entity is dead
   * @returns True if dead
   */
  public isDying(): boolean {
    return this.isDead;
  }
  
  /**
   * Sets the maximum health
   * @param maxHealth The new maximum health
   * @param adjustCurrent Whether to adjust current health proportionally
   */
  public setMaxHealth(maxHealth: number, adjustCurrent: boolean = true): void {
    const oldMax = this.maxHealth;
    this.maxHealth = maxHealth;
    
    if (adjustCurrent) {
      // Adjust current health proportionally
      const ratio = this.currentHealth / oldMax;
      this.currentHealth = Math.floor(this.maxHealth * ratio);
    } else {
      // Cap current health at new max
      this.currentHealth = Math.min(this.currentHealth, this.maxHealth);
    }
  }
  
  /**
   * Adds a callback for when damage is taken
   * @param callback The callback function
   */
  public onDamage(callback: (amount: number) => void): void {
    this.onDamageCallbacks.push(callback);
  }
  
  /**
   * Adds a callback for when the entity dies
   * @param callback The callback function
   */
  public onDeath(callback: () => void): void {
    this.onDeathCallbacks.push(callback);
  }
  
  /**
   * Adds a callback for when the entity is healed
   * @param callback The callback function
   */
  public onHeal(callback: (amount: number) => void): void {
    this.onHealCallbacks.push(callback);
  }
  
  /**
   * Removes a damage callback
   * @param callback The callback to remove
   */
  public removeOnDamage(callback: (amount: number) => void): void {
    const index = this.onDamageCallbacks.indexOf(callback);
    if (index !== -1) {
      this.onDamageCallbacks.splice(index, 1);
    }
  }
  
  /**
   * Removes a death callback
   * @param callback The callback to remove
   */
  public removeOnDeath(callback: () => void): void {
    const index = this.onDeathCallbacks.indexOf(callback);
    if (index !== -1) {
      this.onDeathCallbacks.splice(index, 1);
    }
  }
  
  /**
   * Removes a heal callback
   * @param callback The callback to remove
   */
  public removeOnHeal(callback: (amount: number) => void): void {
    const index = this.onHealCallbacks.indexOf(callback);
    if (index !== -1) {
      this.onHealCallbacks.splice(index, 1);
    }
  }
} 